// src/services/activation.js
import { batchDataChanges, reminders } from './database';
import { services, updateOnboarding, ensureWorkspace } from './productBrain';
import { browserPushStatus } from './browserPush';
import { applyPatch, dayKey, mergeActivation } from '../lib/activation';
import { SAMPLE_KEYS, SAMPLE_ORDER, buildSampleWorkspace } from '../lib/sampleWorkspace';

export const loadActivation = async () => mergeActivation((await ensureWorkspace()).onboarding?.activation);
export const saveActivation = patch => updateOnboarding(onboarding => ({ ...onboarding, activation: applyPatch(onboarding.activation, patch) })).then(o => o.activation);

// IDs are recorded before any record exists, so a half-finished seed is always known and removable.
export async function seedSample(now = new Date(), save = saveActivation) {
  const activation = await save(a => {
    if (a.sample_map) return { path: 'sample' };
    const map = Object.fromEntries(SAMPLE_KEYS.map(key => [key, crypto.randomUUID()]));
    return { path: 'sample', sample_map: map, sample_ids: [...new Set([...a.sample_ids, ...Object.values(map)])] };
  });
  const pending = new Set(activation.sample_ids);
  const records = buildSampleWorkspace(now, activation.sample_map);
  let created = 0;
  await batchDataChanges(async () => {
    for (const service of SAMPLE_ORDER) for (const row of records[service]) {
      if (!pending.has(row.id) || await services[service].getById(row.id)) continue;
      await services[service].create(row);
      created++;
    }
  });
  return { created };
}

export async function clearSample(save = saveActivation) {
  const activation = await loadActivation();
  if (!activation.sample_map) return { removed: 0, failed: [] };
  const records = buildSampleWorkspace(new Date(), activation.sample_map);
  const listed = new Set(activation.sample_ids);
  const failed = [];
  let removed = 0;
  await batchDataChanges(async () => {
    for (const service of [...SAMPLE_ORDER].reverse()) for (const row of records[service]) {
      if (!listed.has(row.id)) continue;
      try { if (await services[service].getById(row.id)) { await services[service].delete(row.id); removed++; } }
      catch { failed.push(row.id); }
    }
  });
  await save({ sample_ids: failed, sample_map: failed.length ? activation.sample_map : null });
  return { removed, failed };
}

export const tomorrowAt = (now, hour) => { const d = new Date(now); d.setDate(d.getDate() + 1); d.setHours(hour, 0, 0, 0); return d; };

// The reminder engine delivers only reminders attached to an initiative.
export async function createGuideReminder({ initiativeId, dueAt, title, description }) {
  const channels = (await browserPushStatus().catch(() => 'unsupported')) === 'enabled' ? ['in_app', 'push'] : ['in_app'];
  return reminders.create({
    roadmap_item_id: initiativeId, title, description, reminder_type: 'status_check',
    target_date: dayKey(dueAt), due_at: dueAt.toISOString(), time_zone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    reminder_days_before: 0, notification_channels: channels, status: 'active',
  });
}

export async function setReviewDate(decisionId, now) {
  const due = new Date(now); due.setDate(due.getDate() + 30);
  const patch = { review_date: dayKey(due) };
  await services.decisions.update(decisionId, patch);
  return patch;
}

export const linkGoal = (initiativeId, objectiveId) => services.initiatives.update(initiativeId, { okr_id: objectiveId });
